import { useEffect, useRef, useState } from 'react';
import API_ENDPOINTS from '../config/api';

/**
 * useOtpLogin — the email-code sign-in, without a look.
 *
 * email → code (six digits, submits itself when complete) → kuutio_session
 * cookie → `onSuccess`. Resend has a 30 s cooldown; the address can be
 * changed without starting over. Two screens draw this: the landing page's
 * CRT card and the front page's plain form.
 */
async function postJson(url, body) {
  const res = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(body),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data.error || 'Jokin meni pieleen — yritä uudelleen');
  return data;
}

export default function useOtpLogin({ onSuccess, initialStep = 'email', initialEmail = '', resendNotice = 'Uusi koodi lähetetty', successNotice = 'Kirjaudutaan...', extra = undefined } = {}) {
  const [step, setStep] = useState(initialStep); // closed | email | code
  const [email, setEmail] = useState(initialEmail || '');
  const [code, setCodeRaw] = useState('');
  const [token, setToken] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [cooldown, setCooldown] = useState(0);
  const lastAutoSubmit = useRef(null);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const id = setTimeout(() => setCooldown((c) => c - 1), 1000);
    return () => clearTimeout(id);
  }, [cooldown]);

  const setCode = (value) => setCodeRaw(String(value).replace(/\D/g, '').slice(0, 6));

  const requestCode = async (isResend = false) => {
    if (busy || !email.includes('@')) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const data = await postJson(API_ENDPOINTS.AUTH_OTP_REQUEST(), { email: email.trim(), ...(extra || {}) });
      setToken(data.token);
      if (!data.token) {
        // The server keeps the same words for known and unknown addresses; the
        // absent token is the honest signal that nothing was sent.
        setError(data.message || 'Jos osoite on sallittu, koodi on lähetetty');
        return;
      }
      setCodeRaw('');
      lastAutoSubmit.current = null;
      setCooldown(30);
      setStep('code');
      if (isResend) setNotice(resendNotice);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async (candidate = code) => {
    if (busy || !token) return;
    setBusy(true); setError(null); setNotice(null);
    try {
      const data = await postJson(API_ENDPOINTS.AUTH_OTP_VERIFY(), { token, code: candidate });
      setNotice(successNotice);
      onSuccess?.(data);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  };

  useEffect(() => {
    if (step === 'code' && code.length === 6 && lastAutoSubmit.current !== code) {
      lastAutoSubmit.current = code;
      verifyCode(code);
    }
  }, [code, step]); // eslint-disable-line react-hooks/exhaustive-deps

  const changeEmail = () => { setStep('email'); setError(null); setCodeRaw(''); };
  const expired = !!error && /vanhentunut/i.test(error);

  return {
    step, setStep, email, setEmail, code, setCode, busy, error, notice, cooldown, expired,
    requestCode, verifyCode, changeEmail,
  };
}
